import { tx } from '../i18n';
import type {
  AnalysisDetailResponse,
  AnalysisSummaryResponse,
  CategoryDetail,
  CategoryName,
  HomeServices,
  VerdictLabel,
} from '../types/api';
import {
  describePercentile,
  formatDate,
  formatNumber as formatNumberLocal,
  formatPounds,
  regionLabel,
} from './format';
import { bool, num, text } from './values';

/** The order the report walks through the categories, most decision-relevant first. */
export const CATEGORY_ORDER: CategoryName[] = ['COMFORT', 'CONNECTIVITY', 'SAFETY'];

/**
 * Categories shown anywhere on screen. Compliance is left out while the GLA register is not
 * loaded; older reports that still carry it are filtered here.
 */
export function isShown(category: CategoryName): boolean {
  return CATEGORY_ORDER.includes(category);
}

export function categoryShort(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx('Comfort', 'Konfor', 'Komfort', 'Conforto', 'Confort');
    case 'CONNECTIVITY':
      return tx('Connectivity', 'Bağlantı', 'Łącze', 'Ligação', 'Conexión');
    case 'SAFETY':
      return tx('Safety', 'Güvenlik', 'Bezpieczeństwo', 'Segurança', 'Seguridad');
    default:
      return tx('Compliance', 'Uygunluk', 'Zgodność', 'Conformidade', 'Cumplimiento');
  }
}

export function categoryTitle(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx('Comfort and energy', 'Konfor ve enerji', 'Komfort i energia', 'Conforto e energia', 'Confort y energía');
    case 'CONNECTIVITY':
      return tx('Broadband and connectivity', 'İnternet ve bağlantı', 'Internet i łącze', 'Internet e ligação', 'Internet y conexión');
    case 'SAFETY':
      return tx('Safety on the street', 'Sokak güvenliği', 'Bezpieczeństwo na ulicy', 'Segurança na rua', 'Seguridad en la calle');
    default:
      return tx('Licensing and compliance', 'Lisans ve uygunluk', 'Licencje i zgodność', 'Licenças e conformidade', 'Licencias y cumplimiento');
  }
}

export function categorySubtitle(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx('EPC, heating, glazing and insulation', 'EPC, ısıtma, pencereler ve yalıtım',
        'EPC, ogrzewanie, okna i izolacja', 'EPC, aquecimento, janelas e isolamento',
        'EPC, calefacción, ventanas y aislamiento');
    case 'CONNECTIVITY':
      return tx('Fixed broadband available at this postcode', 'Bu posta kodundaki sabit internet altyapısı',
        'Internet stacjonarny dostępny pod tym kodem pocztowym',
        'Internet fixa disponível neste código postal',
        'Internet fija disponible en este código postal');
    case 'SAFETY':
      return tx('Recorded crime within 500m over 12 months', '12 ayda 500 m içindeki kayıtlı suçlar',
        'Odnotowana przestępczość w promieniu 500 m w ciągu 12 miesięcy',
        'Crime registado num raio de 500 m em 12 meses',
        'Delincuencia registrada en 500 m en 12 meses');
    default:
      return tx('Property licensing and enforcement records', 'Konut lisansı ve yaptırım kayıtları',
        'Rejestry licencji mieszkaniowych i postępowań',
        'Registos de licenciamento e coimas',
        'Registros de licencias y sanciones');
  }
}

/**
 * The short verdict beside the score.
 *
 * <p>It says what the evidence looks like, not what the reader should do. "Good choice" and
 * "Think twice" are advice, and this product is not in a position to give it: it has read some
 * public registers about an address, and it knows nothing about the rent, the landlord, the
 * tenancy agreement or the state of the kitchen. Describing the indicators keeps the claim the
 * size of the evidence behind it.
 */
export function verdictShort(label: VerdictLabel): string {
  switch (label) {
    case 'EXCELLENT':
      return tx(
        'Indicators are strong',
        'Göstergeler güçlü',
        'Wskaźniki są mocne',
        'Indicadores fortes',
        'Indicadores sólidos',
      );
    case 'GOOD':
      return tx(
        'Indicators are favourable',
        'Göstergeler olumlu',
        'Wskaźniki są korzystne',
        'Indicadores favoráveis',
        'Indicadores favorables',
      );
    case 'FAIR':
      return tx(
        'Indicators are mixed',
        'Göstergeler karışık',
        'Wskaźniki są mieszane',
        'Indicadores mistos',
        'Indicadores desiguales',
      );
    default:
      return tx(
        'Indicators raise concerns',
        'Göstergeler endişe veriyor',
        'Wskaźniki budzą zastrzeżenia',
        'Indicadores preocupantes',
        'Indicadores preocupantes',
      );
  }
}

/**
 * What the verdict covers, and what it does not. Sits under the score, because a number with a
 * word next to it is read as a recommendation unless it says otherwise.
 */
export function verdictScope(): string {
  return tx(
    'Based on public records for this address. It says nothing about the rent, the landlord, the tenancy agreement or the condition inside.',
    'Bu adrese ait kamu kayıtlarına dayanır. Kira, ev sahibi, sözleşme ya da içerideki durum hakkında bir şey söylemez.',
    'Na podstawie rejestrów publicznych dla tego adresu. Nie mówi nic o czynszu, właścicielu, umowie najmu ani o stanie wnętrza.',
    'Baseado em registos públicos desta morada. Não diz nada sobre a renda, o senhorio, o contrato de arrendamento ou o estado interior.',
    'Basado en registros públicos de esta dirección. No dice nada sobre la renta, el propietario, el contrato ni el estado del interior.',
  );
}

/** The bold opening of the verdict box, in the same descriptive register as {@link verdictShort}. */
export function verdictLine(label: VerdictLabel): string {
  switch (label) {
    case 'EXCELLENT':
      return tx('The records are strong throughout.', 'Kayıtlar baştan sona güçlü.', 'Rejestry są mocne w każdym punkcie.', 'Os registos são fortes em todos os pontos.', 'Los registros son sólidos en todo.');
    case 'GOOD':
      return tx('The records are favourable overall.', 'Kayıtlar genel olarak olumlu.', 'Rejestry są ogólnie korzystne.', 'Os registos são globalmente favoráveis.', 'Los registros son favorables en conjunto.');
    case 'FAIR':
      return tx('The records are mixed.', 'Kayıtlar karışık.', 'Rejestry są mieszane.', 'Os registos são mistos.', 'Los registros son desiguales.');
    default:
      return tx('The records raise significant concerns.', 'Kayıtlar ciddi endişeler doğuruyor.', 'Rejestry budzą poważne zastrzeżenia.', 'Os registos levantam preocupações sérias.', 'Los registros plantean preocupaciones serias.');
  }
}

/** What a checklist entry is called, per category. The detail is the scorer's own action. */
function checklistTitle(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx('Request recent energy bills', 'Son enerji faturalarını isteyin',
        'Poproś o ostatnie rachunki za energię', 'Peça faturas de energia recentes',
        'Pide facturas de energía recientes');
    case 'CONNECTIVITY':
      return tx('Check the broadband installation', 'İnternet bağlantısını kontrol edin',
        'Sprawdź instalację internetową', 'Verifique a instalação de internet',
        'Comprueba la instalación de internet');
    case 'SAFETY':
      return tx('Visit the street at different times', 'Sokağı farklı saatlerde ziyaret edin',
        'Odwiedź ulicę o różnych porach', 'Visite a rua a horas diferentes',
        'Visita la calle a distintas horas');
    default:
      return tx('Confirm the property licence', 'Konut lisansını teyit edin',
        'Potwierdź licencję mieszkania', 'Confirme a licença do imóvel',
        'Confirma la licencia de la vivienda');
  }
}

/** Broadband tier from its code; the API keeps an English label in the metrics. */
export function tierLabel(tier: string | null, fallback: string | null): string | null {
  switch (tier) {
    case 'GIGABIT_FTTP':
      return tx('Full fibre / gigabit', 'Fiber / gigabit',
    'Światłowód / gigabit',
    'Fibra ótica / gigabit',
    'Fibra óptica / gigabit');
    case 'SUPERFAST':
      return tx('Superfast', 'Superfast',
    'Superfast',
    'Superfast',
    'Superfast');
    case 'STANDARD_ADSL':
      return tx('Standard broadband', 'Standart internet',
    'Internet standardowy',
    'Internet padrão',
    'Internet estándar');
    default:
      return fallback;
  }
}

/**
 * The storey, as the API composes it from the RdSAP code. The handful of phrasings it can produce
 * are known, so they are translated here rather than shipped twice.
 */
export function floorLevelLabel(value: string): string {
  const named: Record<string, [string, string, string, string]> = {
    Basement: ['Bodrum', 'Piwnica', 'Cave', 'Sótano'],
    'Ground floor': ['Zemin kat', 'Parter', 'Rés do chão', 'Planta baja'],
    '21st floor or above': [
      '21. kat ve üzeri',
      '21. piętro lub wyżej',
      '21.º piso ou acima',
      'Planta 21 o superior',
    ],
  };
  const known = named[value];
  if (known) {
    return tx(value, ...known);
  }
  const ordinal = value.match(/^(\d+)(st|nd|rd|th) floor$/);
  if (!ordinal) {
    return value;
  }
  const floor = ordinal[1];
  return tx(value, `${floor}. kat`, `${floor}. piętro`, `${floor}.º piso`, `Planta ${floor}`);
}

/** EPC property types and built forms, as the register writes them. */
export function propertyTypeLabel(value: string): string {
  const key = value.trim().toLowerCase();
  const names: Record<string, [string, string, string, string]> = {
    flat: ['Daire', 'Mieszkanie', 'Apartamento', 'Piso'],
    house: ['Ev', 'Dom', 'Moradia', 'Casa'],
    maisonette: ['Dubleks daire', 'Mieszkanie dwupoziomowe', 'Duplex', 'Dúplex'],
    bungalow: ['Bungalov', 'Parterowy dom', 'Moradia térrea', 'Casa de una planta'],
    'park home': ['Mobil ev', 'Dom mobilny', 'Casa móvel', 'Casa móvil'],
  };
  const known = names[key];
  return known ? tx(titleCase(value), ...known) : titleCase(value);
}

export function builtFormLabel(value: string): string {
  const key = value.trim().toLowerCase();
  const names: Record<string, [string, string, string, string]> = {
    detached: ['Müstakil', 'Wolnostojący', 'Isolada', 'Aislada'],
    'semi-detached': ['İkiz', 'Bliźniak', 'Geminada', 'Pareada'],
    'mid-terrace': ['Sıra (ara)', 'Szeregowy (środkowy)', 'Em banda (meio)', 'Adosada (central)'],
    'end-terrace': ['Sıra (köşe)', 'Szeregowy (skrajny)', 'Em banda (topo)', 'Adosada (extremo)'],
    'enclosed mid-terrace': [
      'Sıra (kapalı ara)',
      'Szeregowy zamknięty (środkowy)',
      'Em banda fechada (meio)',
      'Adosada cerrada (central)',
    ],
    'enclosed end-terrace': [
      'Sıra (kapalı köşe)',
      'Szeregowy zamknięty (skrajny)',
      'Em banda fechada (topo)',
      'Adosada cerrada (extremo)',
    ],
  };
  const known = names[key];
  return known ? tx(titleCase(value), ...known) : titleCase(value);
}

/**
 * "Flat 3, Seymour Court, Coppetts Road, London" becomes a title of "Flat 3 Seymour Court" and a
 * subtitle of "Coppetts Road, London". A bare door number is joined to the next part, because
 * "3" on its own is not a title.
 */
export function splitAddress(addressLine: string): { title: string; rest: string } {
  const parts = addressLine
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    return { title: addressLine, rest: '' };
  }
  const first = parts[0] ?? '';
  const short = /^(flat|apartment|unit|room|studio)?\s*[\w/-]{1,6}$/i.test(first);
  const titleParts = short && parts.length > 2 ? 2 : 1;
  return {
    title: parts.slice(0, titleParts).join(' '),
    rest: parts.slice(titleParts).join(', '),
  };
}

/** How long ago a report was produced, e.g. "Updated today". */
export function updatedLabel(iso: string | null | undefined, checked = false): string {
  if (!iso) {
    return '';
  }
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) {
    return '';
  }
  const startOf = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOf(new Date()) - startOf(then)) / 86_400_000);

  // The verb differs per language and, in several of them, so does where it sits in the sentence,
  // so each language writes its own line rather than filling a shared template.
  const en = checked ? 'Last checked' : 'Updated';
  const tr = checked ? 'Son kontrol' : 'Güncellendi';
  const pl = checked ? 'Ostatnio sprawdzone' : 'Zaktualizowano';
  const pt = checked ? 'Última verificação' : 'Atualizado';
  const es = checked ? 'Última comprobación' : 'Actualizado';

  if (days <= 0) {
    return tx(
      `${en} today`,
      checked ? `${tr}: bugün` : 'Bugün güncellendi',
      `${pl} dzisiaj`,
      `${pt} hoje`,
      `${es} hoy`,
    );
  }
  if (days === 1) {
    return tx(
      `${en} yesterday`,
      checked ? `${tr}: dün` : 'Dün güncellendi',
      `${pl} wczoraj`,
      `${pt} ontem`,
      `${es} ayer`,
    );
  }
  if (days < 7) {
    return tx(
      `${en} ${days} days ago`,
      checked ? `${tr}: ${days} gün önce` : `${days} gün önce güncellendi`,
      `${pl} ${days} dni temu`,
      `${pt} há ${days} dias`,
      `${es} hace ${days} días`,
    );
  }
  const date = formatDate(iso);
  return tx(`${en} ${date}`, `${tr}: ${date}`, `${pl} ${date}`, `${pt} em ${date}`, `${es} el ${date}`);
}

/**
 * True when the category's data could not be retrieved, so its number is only a placeholder.
 * The API flags it ({@code metrics.unrated}); reports scored before the flag are recognised by
 * their missing measurements.
 */
export function isUnrated(category: CategoryDetail): boolean {
  const m = category.metrics;
  if (m.unrated === true || m.available === false) {
    return true;
  }
  if (category.category === 'SAFETY' && typeof m.totalOffences !== 'number') {
    return true;
  }
  return category.weightPercent === 0 && num(category.percentile) === null;
}

export function byCategory(categories: CategoryDetail[]): Partial<Record<CategoryName, CategoryDetail>> {
  const result: Partial<Record<CategoryName, CategoryDetail>> = {};
  for (const category of categories) {
    result[category.category] = category;
  }
  return result;
}

export function orderedCategories(categories: CategoryDetail[]): CategoryDetail[] {
  const lookup = byCategory(categories);
  return CATEGORY_ORDER.map((name) => lookup[name]).filter(
    (category): category is CategoryDetail => Boolean(category),
  );
}

/*
 * The metric guards live in lib/values.ts now, so the report and the comparison cannot disagree
 * about what counts as a missing number. `str` keeps its old name here; it is `text` there.
 */
const str = text;

function titleCase(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
}

function share(value: number): string {
  return tx(`${Math.round(value)}% of premises`, `binaların %${Math.round(value)}`,
    `${Math.round(value)}% budynków`,
    `${Math.round(value)}% dos edifícios`,
    `${Math.round(value)}% de los inmuebles`);
}


/** The region a category was ranked against; London for reports scored before England. */
/**
 * The one line under a score on the overview tiles: the single fact that explains the number,
 * short enough to sit in a tile. It is not the category's summary sentence, which is written to be
 * read in full further down the page.
 */
export function tileNote(category: CategoryDetail): string {
  if (isUnrated(category)) {
    return tx('Data could not be retrieved', 'Veri alınamadı',
    'Nie udało się pobrać danych',
    'Não foi possível obter dados',
    'No se han podido obtener datos');
  }
  const m = category.metrics;
  switch (category.category) {
    case 'COMFORT': {
      const band = str(m.band);
      const potential = str(m.potentialBand);
      if (band) {
        return potential && potential !== band
          ? tx(`EPC ${band}, could reach ${potential}`, `EPC ${band}, ${potential} olabilir`,
    `EPC ${band}, możliwe ${potential}`,
    `EPC ${band}, pode chegar a ${potential}`,
    `EPC ${band}, podría llegar a ${potential}`)
          : tx(`EPC ${band}`, `EPC ${band}`,
    `EPC ${band}`,
    `EPC ${band}`,
    `EPC ${band}`);
      }
      return tx('No certificate found', 'Sertifika bulunamadı',
    'Nie znaleziono świadectwa',
    'Não foi encontrado certificado',
    'No se ha encontrado certificado');
    }
    case 'CONNECTIVITY': {
      const tier = tierLabel(str(m.tier), str(m.tierLabel));
      const gigabit = num(m.gigabitPct);
      if (tier && gigabit !== null) {
        return tx(`${tier}, ${share(gigabit)}`, `${tier}, ${share(gigabit)} kadarında`,
    `${tier}, ${share(gigabit)}`,
    `${tier}, ${share(gigabit)}`,
    `${tier}, ${share(gigabit)}`);
      }
      return tier ?? tx('Coverage measured nearby', 'Kapsama yakından ölçüldü',
    'Zasięg zmierzony w pobliżu',
    'Cobertura medida nas imediações',
    'Cobertura medida en los alrededores');
    }
    case 'SAFETY': {
      const total = num(m.totalOffences);
      const radius = num(m.radiusMetres) ?? 500;
      if (total !== null) {
        const count = formatNumberLocal(total);
        const rank = num(category.percentile);
        const place = benchmarkArea(category);
        /*
         * The absolute number and the comparison belong in the same sentence.
         * "412 offences within 500 m" beside a tile reading "Quiet for the area" looks like the
         * report contradicting itself; 412 is a large number to anyone who has not also been told
         * what a normal number is for a London postcode. Saying both at once is what makes the
         * score legible - and when there is no ranking to state, the bare count stands alone
         * rather than being dressed up.
         */
        if (rank !== null && place) {
          const area = regionLabel(place);
          return tx(
            `${count} reports in 12 months — quieter than ${rank}% of ${area}`,
            `12 ayda ${count} kayıt — ${regionLabel(place)} genelinin %${rank} kadarından sakin`,
            `${count} zgłoszeń w ciągu 12 miesięcy — spokojniej niż w ${rank}% ${area}`,
            `${count} registos em 12 meses — mais sossegado do que ${rank}% de ${area}`,
            `${count} denuncias en 12 meses — más tranquilo que el ${rank}% de ${area}`,
          );
        }
        return tx(
          `${count} reports within ${radius} m in 12 months`,
          `12 ayda ${radius} m içinde ${count} kayıt`,
          `${count} zgłoszeń w promieniu ${radius} m w ciągu 12 miesięcy`,
          `${count} registos num raio de ${radius} m em 12 meses`,
          `${count} denuncias en un radio de ${radius} m en 12 meses`,
        );
      }
      break;
    }
    default:
      break;
  }
  return describePercentile(category.percentile, benchmarkArea(category))
    ?? tx('Scored against similar homes', 'Benzer konutlara göre puanlandı',
    'Ocenione na tle podobnych mieszkań',
    'Pontuado face a casas semelhantes',
    'Puntuado frente a viviendas similares');
}

/**
 * Where the cut-points behind a percentile came from, said in one line.
 *
 * <p>"Better than 79% of London" reads as something this product measured across London. For now
 * it is not: the cut-points are published figures seeded into the database, waiting to be
 * recomputed from real analysis history. Naming the source and its date is the difference between
 * a comparison somebody can weigh and a number they have to take on trust.
 */
export function benchmarkProvenance(category: CategoryDetail): string | null {
  const source = str(category.metrics.benchmarkSource);
  if (!source) {
    return null;
  }
  const date = str(category.metrics.benchmarkDate);
  const when = date ? formatDate(date) : null;
  return when
    ? tx(
        `Compared against cut-points from ${source}, ${when}`,
        `Karşılaştırma eşikleri: ${source}, ${when}`,
        `Porównanie do progów z ${source}, ${when}`,
        `Comparado com limiares de ${source}, ${when}`,
        `Comparado con umbrales de ${source}, ${when}`,
      )
    : tx(
        `Compared against cut-points from ${source}`,
        `Karşılaştırma eşikleri: ${source}`,
        `Porównanie do progów z ${source}`,
        `Comparado com limiares de ${source}`,
        `Comparado con umbrales de ${source}`,
      );
}

export function benchmarkArea(category: CategoryDetail): string | null {
  const area = str(category.metrics.benchmarkArea);
  if (area) {
    return area;
  }
  // Reports scored before England was covered carry no area, and were all ranked against London.
  return num(category.percentile) === null ? null : 'London';
}

export function firstSentence(text: string): string {
  const match = text.match(/^.*?[.!?](\s|$)/);
  return (match ? match[0] : text).trim();
}


export interface FactItem {
  term: string;
  value: string;
}

/** The key facts grid for one category, from its metrics and, for comfort, the heating record. */
export function categoryFacts(category: CategoryDetail, services: HomeServices | null | undefined): FactItem[] {
  const m = category.metrics;
  const facts: FactItem[] = [];
  const push = (term: string, value: string | null) => {
    if (value) {
      facts.push({ term, value });
    }
  };

  switch (category.category) {
    case 'COMFORT': {
      const band = str(m.band);
      const potential = str(m.potentialBand);
      const sap = num(m.sapPoints);
      push(
        tx('EPC rating', 'EPC notu',
    'Klasa EPC',
    'Classe EPC',
    'Letra EPC'),
        band
          ? [
              band,
              sap !== null ? tx(`${sap} points`, `${sap} puan`,
    `${sap} pkt`,
    `${sap} pontos`,
    `${sap} puntos`) : null,
              potential && potential !== band ? tx(`could reach ${potential}`, `${potential} olabilir`,
    `możliwe ${potential}`,
    `pode chegar a ${potential}`,
    `podría llegar a ${potential}`) : null,
            ]
              .filter(Boolean)
              .join(' · ')
          : tx('No certificate found', 'Sertifika bulunamadı',
    'Nie znaleziono świadectwa',
    'Não foi encontrado certificado',
    'No se ha encontrado certificado'),
      );
      push(tx('Heating', 'Isıtma',
    'Ogrzewanie',
    'Aquecimento',
    'Calefacción'), services?.energy?.mainHeating ?? null);
      const single = bool(m.singleGlazing);
      push(
        tx('Windows', 'Pencereler',
    'Okna',
    'Janelas',
    'Ventanas'),
        single === null ? null : single ? tx('Some single glazing', 'Kısmen tek cam',
    'Częściowo pojedyncze szyby',
    'Alguns vidros simples',
    'Algo de vidrio simple') : tx('Double glazed', 'Çift cam',
    'Szyby zespolone',
    'Vidros duplos',
    'Doble acristalamiento'),
      );
      const walls = bool(m.uninsulatedWalls);
      push(
        tx('Walls', 'Duvarlar',
    'Ściany',
    'Paredes',
    'Muros'),
        walls === null ? null : walls ? tx('Uninsulated', 'Yalıtımsız',
    'Nieocieplone',
    'Sem isolamento',
    'Sin aislamiento') : tx('Insulated', 'Yalıtımlı',
    'Ocieplone',
    'Com isolamento',
    'Con aislamiento'),
      );
      const cost = num(m.annualEnergyCostGbp);
      push(
        tx('Estimated bills', 'Tahmini faturalar',
    'Szacowane rachunki',
    'Faturas estimadas',
    'Facturas estimadas'),
        cost !== null
          ? tx(
              `${formatPounds(cost / 12)} a month (${formatPounds(cost)} a year)`,
              `Ayda ${formatPounds(cost / 12)} (yılda ${formatPounds(cost)})`,
    `${formatPounds(cost / 12)} miesięcznie (${formatPounds(cost)} rocznie)`,
    `${formatPounds(cost / 12)} por mês (${formatPounds(cost)} por ano)`,
    `${formatPounds(cost / 12)} al mes (${formatPounds(cost)} al año)`,
            )
          : null,
      );
      const area = num(m.totalFloorArea);
      push(tx('Floor area', 'Kullanım alanı',
    'Powierzchnia',
    'Área',
    'Superficie'), area !== null ? `${Math.round(area)} m²` : null);
      if (category.sourceDate) {
        push(tx('Certificate lodged', 'Sertifika tarihi',
    'Data świadectwa',
    'Data do certificado',
    'Fecha del certificado'), formatDate(category.sourceDate));
      }
      break;
    }
    case 'CONNECTIVITY': {
      push(tx('Best available', 'En iyi seçenek',
    'Najlepsza opcja',
    'Melhor disponível',
    'Mejor opción'), tierLabel(str(m.tier), str(m.tierLabel)));
      const gigabit = num(m.gigabitPct);
      push(tx('Full fibre / gigabit', 'Fiber / gigabit',
    'Światłowód / gigabit',
    'Fibra ótica / gigabit',
    'Fibra óptica / gigabit'), gigabit !== null ? share(gigabit) : null);
      const superfast = num(m.superfastPct);
      push('Superfast (30 Mbit/s+)', superfast !== null ? share(superfast) : null);
      const speed = num(m.avgDownloadMbps);
      push(tx('Average download', 'Ortalama indirme',
    'Średnie pobieranie',
    'Descarga média',
    'Bajada media'), speed !== null ? `${Math.round(speed)} Mbit/s` : null);
      const below = num(m.belowUsoPct);
      push(tx('Below 10 Mbit/s', '10 Mbit/s altı',
    'Poniżej 10 Mb/s',
    'Abaixo de 10 Mbit/s',
    'Por debajo de 10 Mbit/s'), below !== null && below > 0 ? share(below) : null);
      const match = str(m.matchLevel);
      const level: Record<string, [string, string, string, string, string]> = {
        POSTCODE: [
          'Postcode level', 'Posta kodu düzeyinde', 'Poziom kodu pocztowego',
          'Ao nível do código postal', 'A nivel de código postal',
        ],
        OUTCODE: [
          'Postcode area level', 'Posta kodu bölgesi düzeyinde', 'Poziom obszaru kodu pocztowego',
          'Ao nível da área do código postal', 'A nivel de área del código postal',
        ],
        BOROUGH: [
          'Borough level', 'İlçe düzeyinde', 'Poziom dzielnicy',
          'Ao nível do município', 'A nivel de municipio',
        ],
      };
      const levelText = match ? level[match.toUpperCase()] : undefined;
      push(tx('Measured at', 'Ölçüm',
    'Poziom pomiaru',
    'Medido ao nível',
    'Medido a nivel'), levelText ? tx(...levelText) : match);
      break;
    }
    case 'SAFETY': {
      const radius = num(m.radiusMetres) ?? 500;
      const total = num(m.totalOffences);
      push(
        tx(`Crimes within ${radius}m`, `${radius} m içindeki suçlar`,
    `Przestępstwa w promieniu ${radius} m`,
    `Crimes num raio de ${radius} m`,
    `Delitos en ${radius} m`),
        total !== null ? tx(`${total} in 12 months`, `12 ayda ${total}`,
    `${total} w ciągu 12 miesięcy`,
    `${total} em 12 meses`,
    `${total} en 12 meses`) : null,
      );
      const violent = num(m.violentOffences);
      push(tx('Violence and weapon offences', 'Şiddet ve silah suçları',
    'Przemoc i przestępstwa z bronią',
    'Violência e crimes com arma',
    'Violencia y delitos con arma'), violent !== null ? String(violent) : null);
      const theft = num(m.theftOffences);
      push(tx('Theft and burglary', 'Hırsızlık',
    'Kradzieże i włamania',
    'Furtos e assaltos',
    'Hurtos y robos en viviendas'), theft !== null ? String(theft) : null);
      const asb = num(m.antiSocialOffences);
      push(tx('Anti-social behaviour', 'Asayiş olayları',
    'Zakłócanie porządku',
    'Incivilidades',
    'Incivismo'), asb !== null ? String(asb) : null);
      const covered = num(m.monthsCovered);
      const requested = num(m.monthsRequested);
      if (covered !== null && requested !== null && covered < requested) {
        push(
          tx('Months of data', 'Veri dönemi',
    'Miesiące danych',
    'Meses de dados',
    'Meses de datos'),
          tx(`${covered} of ${requested}, scaled up`, `${requested} ayın ${covered} ayı, ölçeklendi`,
    `${covered} z ${requested}, przeskalowane`,
    `${covered} de ${requested}, ajustado`,
    `${covered} de ${requested}, escalado`),
        );
      }
      break;
    }
    default:
      break;
  }
  return facts;
}

export interface ReportAlert {
  title: string;
  detail: string;
}

/** The single most important thing to check, shown as a banner under the hero. */
export function topAlert(
  summary: AnalysisSummaryResponse,
  detail: AnalysisDetailResponse | null,
): ReportAlert | null {
  if (summary.dataNotice) {
    return { title: tx('About this data', 'Bu veriler hakkında',
    'O tych danych',
    'Sobre estes dados',
    'Sobre estos datos'), detail: summary.dataNotice };
  }
  if (!detail) {
    return null;
  }
  for (const severity of ['WARNING', 'ATTENTION'] as const) {
    for (const category of orderedCategories(detail.categories)) {
      const finding = category.findings.find((item) => item.severity === severity);
      if (finding) {
        return {
          title: tx('One important check before you proceed', 'İlerlemeden önce önemli bir kontrol',
    'Jedna ważna rzecz do sprawdzenia',
    'Uma verificação importante antes de avançar',
    'Una comprobación importante antes de seguir'),
          detail: finding.detail ? `${finding.title}. ${finding.detail}` : finding.title,
        };
      }
    }
  }
  return null;
}

export interface ChecklistItem {
  category: CategoryName;
  title: string;
  detail: string;
}

/**
 * What to put to whoever is letting or selling it, one entry per category that
 * leaves something open.
 *
 * <p>A category the scorer had an action for contributes that action. A category whose source
 * could not be read contributes a question too, and this is the important half: the gap is exactly
 * where a tenant is most exposed, and an empty section is easier to walk past than a bad number.
 * So every risky or missing thing on the report leaves with a question attached to it.
 */
export function checklist(detail: AnalysisDetailResponse): ChecklistItem[] {
  const lookup = byCategory(detail.categories);
  const items: ChecklistItem[] = [];
  for (const name of CATEGORY_ORDER) {
    const category = lookup[name];
    if (!category) {
      continue;
    }
    if (category.action) {
      items.push({ category: name, title: checklistTitle(name), detail: category.action });
    } else if (isUnrated(category)) {
      items.push({ category: name, title: missingTitle(name), detail: missingQuestion(name) });
    }
  }
  return items;
}

/** What to call the question raised by a source that could not be read. */
function missingTitle(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx('Ask for the energy certificate', 'Enerji sertifikasını isteyin',
        'Poproś o świadectwo energetyczne', 'Peça o certificado energético',
        'Pide el certificado energético');
    case 'CONNECTIVITY':
      return tx('Ask what broadband is installed', 'Hangi internet altyapısı var, sorun',
        'Zapytaj, jaki internet jest podłączony', 'Pergunte que internet está instalada',
        'Pregunta qué internet hay instalada');
    case 'SAFETY':
      return tx('Ask the neighbours about the street', 'Sokağı komşulara sorun',
        'Zapytaj sąsiadów o ulicę', 'Pergunte aos vizinhos sobre a rua',
        'Pregunta a los vecinos por la calle');
    default:
      return tx('Ask for the paperwork', 'Belgeleri isteyin',
        'Poproś o dokumenty', 'Peça a documentação', 'Pide la documentación');
  }
}

/** The question itself, written so it can be sent to an agent or seller unedited. */
function missingQuestion(category: CategoryName): string {
  switch (category) {
    case 'COMFORT':
      return tx(
        'No energy certificate could be found for this address. A home cannot legally be let without a valid EPC, so ask for its certificate number and its band before you pay anything.',
        'Bu adres için enerji sertifikası bulunamadı. Geçerli bir EPC olmadan konut yasal olarak kiraya verilemez; ödeme yapmadan önce sertifika numarasını ve bandını isteyin.',
        'Nie znaleziono świadectwa energetycznego dla tego adresu. Mieszkania nie można legalnie wynająć bez ważnego EPC — poproś o numer świadectwa i klasę, zanim cokolwiek zapłacisz.',
        'Não foi encontrado certificado energético para esta morada. Uma casa não pode ser arrendada sem um certificado válido, por isso peça o número e a classe antes de pagar seja o que for.',
        'No se ha encontrado certificado energético para esta dirección. Una vivienda no se puede alquilar legalmente sin un certificado válido, así que pide el número y la letra antes de pagar nada.',
      );
    case 'CONNECTIVITY':
      return tx(
        'Ofcom has no coverage figure for this postcode. Ask which provider the property is connected to today, and whether the line is already live.',
        'Ofcom bu posta kodu için kapsama verisi yayımlamıyor. Konutun bugün hangi sağlayıcıya bağlı olduğunu ve hattın aktif olup olmadığını sorun.',
        'Ofcom nie ma danych o zasięgu dla tego kodu pocztowego. Zapytaj, do jakiego dostawcy lokal jest dziś podłączony i czy łącze jest aktywne.',
        'A Ofcom não tem dados de cobertura para este código postal. Pergunte a que operador o imóvel está ligado hoje e se a linha já está ativa.',
        'Ofcom no publica cobertura para este código postal. Pregunta a qué operador está conectada la vivienda hoy y si la línea ya está activa.',
      );
    case 'SAFETY':
      return tx(
        'The police data for this address could not be read, so there is no crime figure in this report. Walk the street after dark, and ask a neighbour how the road feels at night.',
        'Bu adres için polis verisi okunamadı; raporda suç rakamı yok. Sokağı hava karardıktan sonra yürüyün ve bir komşuya geceleri sokağın nasıl olduğunu sorun.',
        'Nie udało się odczytać danych policji dla tego adresu, więc w raporcie nie ma liczby przestępstw. Przejdź się ulicą po zmroku i zapytaj sąsiada, jak tu jest w nocy.',
        'Não foi possível ler os dados da polícia para esta morada, pelo que não há números de criminalidade neste relatório. Percorra a rua depois de escurecer e pergunte a um vizinho como é a rua à noite.',
        'No se han podido leer los datos policiales de esta dirección, así que no hay cifras de delincuencia en el informe. Recorre la calle de noche y pregunta a un vecino cómo se vive la calle a esas horas.',
      );
    default:
      return tx(
        'This register could not be read for the address. Ask the landlord, agent or seller to show the paperwork instead.',
        'Bu kayıt adres için okunamadı. Ev sahibinden, emlakçıdan ya da satıcıdan belgeleri göstermesini isteyin.',
        'Nie udało się odczytać tego rejestru dla adresu. Poproś właściciela, pośrednika lub sprzedającego o okazanie dokumentów.',
        'Não foi possível ler este registo para a morada. Peça ao senhorio, à agência ou ao vendedor que mostre a documentação.',
        'No se ha podido leer este registro para la dirección. Pide al propietario, a la agencia o al vendedor que enseñe la documentación.',
      );
  }
}
